import React, { useCallback, useEffect, useState } from 'react';
import * as hr from '../../api';
import { fmtMoney } from '../../format';
import { Chip } from '../attendance/shared';

// HR Request Center: shared labels, tones and small parts (Stephanet's custom/hrrc/shared.jsx). The server answers status
// codes (pending_approval …) with their labels alongside; the chips show the labels.

export const STATUS_TONE = {
  draft: 'gray', submitted: 'blue', pending_approval: 'yellow', in_review: 'purple', approved: 'green', rejected: 'red',
  sent_back: 'orange', completed: 'teal', cancelled: 'gray',
};
export const STATUS_LABEL = {
  draft: 'Draft', submitted: 'Submitted', pending_approval: 'Pending Approval', in_review: 'In Review', approved: 'Approved', rejected: 'Rejected',
  sent_back: 'Sent Back', completed: 'Completed', cancelled: 'Cancelled',
};
export const PRIORITY_TONE = { Low: 'gray', Medium: 'blue', High: 'orange', Urgent: 'red', Critical: 'darkred' };
export const StatusChip = ({ s }) => (s ? <Chip tone={STATUS_TONE[s]}>{STATUS_LABEL[s] || s}</Chip> : null);
export const PriorityChip = ({ p }) => <Chip tone={PRIORITY_TONE[p]}>{p}</Chip>;

// ₦1435K, as the reference shows amounts in the request lists
export const kNaira = v => (v ? (v > 1000 ? `₦${(v / 1000).toFixed(0)}K` : `₦${v}`) : '—');
export const naira = v => (v || v === 0 ? fmtMoney(v) : '—');

// the module / type lookups the screens use, from the meta's lists
export const findModule = (meta, code) => meta?.modules.find(m => m.code === code);
export const findType = (meta, typeId) => meta?.modules.flatMap(m => m.types).find(t => String(t.id) === String(typeId));
export const typeModule = (meta, typeId) => meta?.modules.find(m => m.types.some(t => String(t.id) === String(typeId)));

export function slaFor(type, priority, priorities) {
  const own = type?.sla?.[priority];
  const days = own ?? priorities?.find(p => p.name === priority)?.sla ?? 0;
  return { days, text: days === 0 ? 'Same day' : `${days} working day${days === 1 ? '' : 's'}` };
}

let metaCache = null;
export function useHrrcMeta() {
  const [meta, setMeta] = useState(metaCache);
  const [error, setError] = useState('');
  const reload = useCallback(() => hr.hrrcMeta().then(m => { metaCache = m; setMeta(m); return m; }).catch(e => setError(e.message)), []);
  useEffect(() => { reload(); }, [reload]);
  return { meta, error, reload };
}

export function Banner({ title, text, children }) {
  return (
    <div className="mdr-top hrc-top">
      <div><h2>{title}</h2><p>{text}</p></div>
      {children && <div className="mdr-top-actions no-print">{children}</div>}
    </div>
  );
}

export const Empty = ({ cols, children }) => <tr><td colSpan={cols} className="mdr-empty">{children}</td></tr>;

export const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

// a print window with the company letterhead
export function printHtml(title, body) {
  const w = window.open('', '_blank');
  if (!w) return;
  const logo = `${window.location.origin}/rnl-logo.png`;
  w.document.write(`<!DOCTYPE html><html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,sans-serif;padding:28px;font-size:11px;color:#111}h1{font-size:16px;margin:0 0 4px}h2{font-size:12px;margin:16px 0 6px;text-transform:uppercase;border-bottom:1px solid #ddd;padding-bottom:4px}
    .lh{display:flex;align-items:center;gap:14px;padding-bottom:10px;margin-bottom:16px;border-bottom:3px solid #0b318b}.lh img{height:58px}
    .lh .co{flex:1}.lh .co b{display:block;font-size:19px;color:#0b318b;text-transform:uppercase}.lh .doc{text-align:right;font-size:10px;color:#555}
    table{width:100%;border-collapse:collapse;margin-top:8px}th,td{border:1px solid #ccc;padding:5px 7px;font-size:10px;text-align:left}th{background:#f0f0f0}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 18px}.l{font-size:9px;color:#666;text-transform:uppercase}.v{font-weight:600}
    .sign{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-top:30px;text-align:center}.sign div{border-top:1px solid #000;padding-top:6px;font-size:10px}
    .muted{color:#888}.tot td{font-weight:bold;background:#f6f6f6}@media print{body{padding:0}}</style></head><body>
    <div class="lh"><img src="${logo}" alt="" onerror="this.style.display='none'" /><div class="co"><b>Rifitel Nigeria Limited</b></div><div class="doc">${esc(title)}</div></div>
    ${body}<p class="muted" style="text-align:center;margin-top:18px;font-size:9px">Printed ${new Date().toLocaleString()}</p></body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 300);
}
